import {
  AcUnit,
  Block,
  CheckCircleOutline,
  Delete,
  Description,
  Public,
  RestoreFromTrash,
  Schedule,
  Visibility,
} from '@mui/icons-material';
import type { FieldValidator, OpenDialog } from '@prismamedia/one-components';
import { ScheduleDialog } from '@prismamedia/one-components';
import {
  GetRecipeQuery_recipe_Recipe,
  RecipeStatus as RecipeStatusId,
} from '__generated__/queries-recipe';
import { ReactNode } from 'react';
import { getFrontUrl } from './getFrontUrl';
import { publishMandatoryFields, saveMandatoryFields } from './mandatoryFields';

export enum RecipeActionId {
  UNPUBLISH = 'UNPUBLISH',
  SEE_FRONT = 'SEE_FRONT',
  SEND_PUSH = 'SEND_PUSH',
  PUBLISH = 'PUBLISH',
  SCHEDULE = 'SCHEDULE',
  SET_AS_READY = 'SET_AS_READY',
  FREEZE = 'FREEZE',
  SET_AS_DRAFT = 'SET_AS_DRAFT',
  UNSCHEDULE = 'UNSCHEDULE',
  RESCHEDULE = 'RESCHEDULE',
  DELETE = 'DELETE',
  RESTORE = 'RESTORE',
}

export interface RecipeAction {
  id: RecipeActionId;
  title: string;
  icon?: ReactNode;
  callBack?: (props?: {
    recipe?: GetRecipeQuery_recipe_Recipe;
    dialog?: {
      closeDialog: { (): void };
      openDialog: OpenDialog;
    };
    schedule?: (dateTime: Date) => Promise<void>;
  }) => void;
  goToStatus?: RecipeStatusId;
  disableStatusChange?: boolean;
  successNotification?: string;
}

export interface RecipeStatus {
  id: RecipeStatusId;
  textPlural: string;
  textSingular: string;
  actions: RecipeActionId[];
  mandatoryFields?: FieldValidator<GetRecipeQuery_recipe_Recipe>[];
}

const recipeActions: RecipeAction[] = [
  {
    id: RecipeActionId.UNPUBLISH,
    title: 'Dépublier',
    icon: <Block />,
    goToStatus: RecipeStatusId.Draft,
    successNotification: ' a été dépublié',
  },
  {
    id: RecipeActionId.SET_AS_DRAFT,
    title: 'Mettre en brouillon',
    icon: <Description />,
    goToStatus: RecipeStatusId.Draft,
    successNotification: ' a été mis en brouillon',
  },
  {
    id: RecipeActionId.SEE_FRONT,
    title: 'Visualiser',
    icon: <Visibility />,
    callBack: (props) => {
      getFrontUrl(props?.recipe) &&
        window.open(getFrontUrl(props?.recipe), '_blank');
    },
  },
  {
    id: RecipeActionId.PUBLISH,
    title: 'Publier',
    icon: <Public />,
    goToStatus: RecipeStatusId.Published,
    successNotification: ' a été publié',
  },
  {
    id: RecipeActionId.UNSCHEDULE,
    title: 'Déprogrammer',
    icon: <Block />,
    goToStatus: RecipeStatusId.Draft,
    successNotification: ' a été déprogrammé',
  },
  {
    id: RecipeActionId.RESCHEDULE,
    title: 'Re-programmer',
    icon: <Schedule />,
    goToStatus: RecipeStatusId.Scheduled,
    disableStatusChange: true,
    callBack: (props) => {
      props?.dialog?.openDialog(
        <ScheduleDialog
          closeDialog={props?.dialog.closeDialog}
          schedule={props?.schedule}
        />,
      );
    },
  },
  {
    id: RecipeActionId.SCHEDULE,
    title: 'Programmer',
    icon: <Schedule />,
    goToStatus: RecipeStatusId.Scheduled,
    disableStatusChange: true,
    callBack: (props) => {
      props?.dialog?.openDialog(
        <ScheduleDialog
          closeDialog={props?.dialog.closeDialog}
          schedule={props?.schedule}
        />,
      );
    },
  },
  {
    id: RecipeActionId.SET_AS_READY,
    title: 'Mettre en prêt pour relecture',
    icon: <CheckCircleOutline />,
    goToStatus: RecipeStatusId.Ready,
    successNotification: ' a été mis en prêt pour relecture',
  },
  {
    id: RecipeActionId.FREEZE,
    title: 'Geler',
    icon: <AcUnit />,
    goToStatus: RecipeStatusId.Frozen,
    successNotification: ' a été gelé',
  },
  {
    id: RecipeActionId.DELETE,
    title: 'Supprimer',
    icon: <Delete />,
    goToStatus: RecipeStatusId.Deleted,
    successNotification: ' a été supprimé',
  },
  {
    id: RecipeActionId.RESTORE,
    title: 'Restaurer',
    icon: <RestoreFromTrash />,
    goToStatus: RecipeStatusId.Draft,
    successNotification: ' a été restauré',
  },
];

export const recipeStatus: RecipeStatus[] = [
  {
    id: RecipeStatusId.Published,
    textPlural: 'Publiés',
    textSingular: 'Publié',
    actions: [RecipeActionId.SEE_FRONT, RecipeActionId.UNPUBLISH],
    mandatoryFields: publishMandatoryFields,
  },
  {
    id: RecipeStatusId.Scheduled,
    textPlural: 'Programmés',
    textSingular: 'Programmé',
    actions: [
      RecipeActionId.SEE_FRONT,
      RecipeActionId.RESCHEDULE,
      RecipeActionId.UNSCHEDULE,
    ],
    mandatoryFields: publishMandatoryFields,
  },
  {
    id: RecipeStatusId.Ready,
    textPlural: 'Prêts',
    textSingular: 'Prêt',
    actions: [
      RecipeActionId.PUBLISH,
      RecipeActionId.SCHEDULE,
      RecipeActionId.FREEZE,
      RecipeActionId.SET_AS_DRAFT,
    ],
    mandatoryFields: saveMandatoryFields,
  },
  {
    id: RecipeStatusId.Frozen,
    textPlural: 'Gelés',
    textSingular: 'Gelé',
    actions: [
      RecipeActionId.PUBLISH,
      RecipeActionId.SCHEDULE,
      RecipeActionId.SET_AS_READY,
      RecipeActionId.SET_AS_DRAFT,
    ],
    mandatoryFields: saveMandatoryFields,
  },
  {
    id: RecipeStatusId.Draft,
    textPlural: 'Brouillons',
    textSingular: 'Brouillon',
    actions: [
      RecipeActionId.PUBLISH,
      RecipeActionId.SCHEDULE,
      RecipeActionId.SET_AS_READY,
      RecipeActionId.FREEZE,
      RecipeActionId.DELETE,
    ],
    mandatoryFields: saveMandatoryFields,
  },
  {
    id: RecipeStatusId.Deleted,
    textPlural: 'Corbeille',
    textSingular: 'Corbeille',
    actions: [RecipeActionId.RESTORE],
    mandatoryFields: saveMandatoryFields,
  },
];

export const getAction = (id: RecipeActionId) =>
  recipeActions.find((action) => action.id === id);

export const getStatus = (id?: string | null) =>
  recipeStatus.find((s) => id === s.id);
